import { createColumnHelper } from '@tanstack/react-table';
import { MoreHorizontal, History, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import type { UserWithRolesDto } from '../types';
import { useAuthorizationStore } from '../store';
import { RolesCell } from './RolesCell';

// Note: Badge is still used by StatusBadge component

const columnHelper = createColumnHelper<UserWithRolesDto>();

// Action cell component
function ActionCell({ user }: { user: UserWithRolesDto }) {
  const openUserRoleHistoryModal = useAuthorizationStore(
    (state) => state.openUserRoleHistoryModal
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => openUserRoleHistoryModal(user)}>
          <History className="h-4 w-4 mr-2" />
          View History
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive"
          disabled
          title="Coming soon"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Revoke Session
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Selection checkbox cell
function SelectionCell({
  user,
  isSelected,
}: {
  user: UserWithRolesDto;
  isSelected: boolean;
}) {
  const toggleUserSelection = useAuthorizationStore(
    (state) => state.toggleUserSelection
  );

  return (
    <Checkbox
      checked={isSelected}
      onCheckedChange={() => toggleUserSelection(user.userId)}
      aria-label={`Select ${user.username}`}
    />
  );
}

// Status badge
function StatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <Badge variant={isActive ? 'success' : 'secondary'}>
      {isActive ? 'Active' : 'Inactive'}
    </Badge>
  );
}

export function createUserRolesColumns(selectedUserIds: number[]) {
  return [
    columnHelper.display({
      id: 'select',
      header: () => {
        const { selectAllUsers, clearUserSelection, usersWithRoles } =
          useAuthorizationStore.getState();
        const allSelected =
          usersWithRoles.length > 0 &&
          selectedUserIds.length === usersWithRoles.length;

        return (
          <Checkbox
            checked={allSelected}
            onCheckedChange={(checked) =>
              checked ? selectAllUsers() : clearUserSelection()
            }
            aria-label="Select all"
          />
        );
      },
      cell: ({ row }) => (
        <SelectionCell
          user={row.original}
          isSelected={selectedUserIds.includes(row.original.userId)}
        />
      ),
    }),
    columnHelper.accessor('username', {
      header: 'Username',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor(
      (row) => `${row.firstName} ${row.lastName}`.trim(),
      {
        id: 'fullName',
        header: 'Full Name',
        cell: (info) => info.getValue() || '-',
      }
    ),
    columnHelper.accessor('email', {
      header: 'Email',
      cell: (info) => info.getValue(),
    }),
    columnHelper.display({
      id: 'roles',
      header: 'Roles',
      cell: ({ row }) => <RolesCell roles={row.original.roles} />,
    }),
    columnHelper.accessor('isActive', {
      header: 'Status',
      cell: (info) => <StatusBadge isActive={info.getValue()} />,
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: ({ row }) => <ActionCell user={row.original} />,
    }),
  ];
}
